import { useCallback, useMemo, useState } from 'react';

import { OrganizationTypes } from '@/common/constants';
import { useCurrentOrganization } from '@/modules/core/hooks/useCurrentOrganization';
import { useGetReactivationEligiblePlans } from '@/modules/employer/components/EmployerReactivation/EmployerReactivationCardAndPlans/queries/useGetReactivationEligiblePlans.query';
import { getIdsFromPath } from '@/utils';

import usePlansReactivation from '../../queries/usePlansReactivation.query';
import { usePlanReactivationModalStore } from '../../stores';
import { ReactivationSteps } from '../../types';

export const useBulkPlanReactivationModal = () => {
  const [loading, setLoading] = useState(false);
  const [isServerError, setIsServerError] = useState(false);
  
  const {
    observingOrganization: { type, id: observingOrganizationId, path },
  } = useCurrentOrganization();

  const { employerId } = getIdsFromPath(path);

  const isSubsidiary = useMemo(() => type === OrganizationTypes.subsidiary, [type]);

  const organizationId = useMemo(
    () => (isSubsidiary ? employerId : observingOrganizationId),
    [isSubsidiary, employerId, observingOrganizationId]
  );

  const currentId = useMemo(() => organizationId || '0', [organizationId]);

  const planIds = usePlanReactivationModalStore((state) => state.planIds);
  const handleSetStep = usePlanReactivationModalStore((state) => state.handleSetStep);
  
  const { plans, isLoading: isLoadingPlans } = useGetReactivationEligiblePlans(currentId);

  const isNoPlans = useMemo(() => plans.length === 0, [plans]);

  const reactivatePlans = usePlansReactivation();

  const onConfirm = useCallback(async () => {
    if (!planIds?.length) return;
    
    setLoading(true);
    try {
      await reactivatePlans({ planIds });
      handleSetStep(ReactivationSteps.RESUME);
    } catch {
      setIsServerError(true);
    } finally {
      setLoading(false);
    }
  }, [planIds, handleSetStep, reactivatePlans]);

  return {
    plans,
    isLoadingPlans,
    loading,
    isServerError,
    setIsServerError,
    onConfirm,
    isNoPlans,
  };
};
